import { NgModule } from "@angular/core";
import { RouterModule, Routes, UrlSerializer } from "@angular/router";
import { AccessDeniedComponent } from "./error-pages/access-denied.component";
import { PermitResolver } from "./forests/services/permit-resolver.service";
import { ForestProductResolver } from "./forests/services/forest-product-resolver.service";
import { ForestsResolver } from "./forests/services/forests-resolver.service";
import { LandingPageComponent } from "./pay-gov-mocks/landing-page/landing-page.component";
import { LoggedInComponent } from "./authentication/logged-in.component";
import { NotFoundComponent } from "./error-pages/not-found/not-found.component";
import { StyleGuideComponent } from "./style-guide/style-guide.component";
import { OrderConfirmationComponent } from "./forests/order-confirmation/order-confirmation.component";
import { UserResolver } from "./user-resolver.service";
import { ForestsAdminResolver } from "./forests/admin/forests-admin-resolver.service";
import { MainLandingComponent } from "./main-landing/main-landing.component";
import { AboutComponent } from "./about/about.component";
import { BuyPermitsComponent } from "./buy-permits/buy-permits.component";
import { SignInComponent } from "./authentication/sign-in.component";
import { FeedbackComponent } from "./feedback/feedback.component";
import { AdminLoginComponent } from './admin/login/login.component'
import { AdminPermitsComponent } from './admin/permits/permits.component'
import { AdminUsersComponent } from './admin/users/users.component'
import { AdminManageUsersComponent } from "./admin/manage-users/manage-users.component";
import { FaqComponent } from "./faq/faq.component";
import { AdminGuard } from "./shared/guards/admin.guard";
import { UserManagementGuard } from "./shared/guards/user-management.guard";
import { PayGovErrorComponent } from "./error-pages/paygov-error/paygov-error.component";
import { ServerErrorComponent } from "./error-pages/server-error/server-error.component";
import { LowerCaseAdminUrlSerializer } from "./shared/url-serializer";
import { PermitManagementGuard } from "./shared/guards/permit-management.guard";
import { CanDeactivateGuard } from "./shared/guards/can-deactivate-guard";
import { SiteSettingsComponent } from "./site-settings/site-settings.component";
import { SiteSettingsGuard } from "./shared/guards/site-settings.guard";
import { ManageAlertsComponent } from "./admin/manage-alerts/manage-alerts.component";
import { SupportUserGuard } from "./shared/guards/support-user.guard";
import { AdminManagePermitsComponent } from "./admin/manage-permits/manage-permits.component";
import { SiteSettingsResolver } from "./site-settings/site-settings.resolver";
import { ModifyForestComponent } from "./site-settings/forests/modify-forest/modify-forest.component";

const appRoutes: Routes = [
  {
    path: '',
    children: [
      {
        path: '',
        title: 'USDA Forest Service E-Permits - Homepage',
        component: MainLandingComponent,
        resolve: {
          forests: ForestsResolver
        },
      },
      {
        path: 'about',
        title: 'USDA Forest Service E-Permits - About',
        component: AboutComponent,
      },
      {
        path: 'contact-us',
        title: 'USDA Forest Service E-Permits - Contact Us',
        component: FeedbackComponent,
      },
      {
        path: 'help',
        title: 'USDA Forest Service E-Permits - Help',
        component: FaqComponent,
      },
      {
        path: 'admin',
        component: AdminLoginComponent,
      },
      {
        path: 'forests/:id/products/:product',
        resolve: {
          forest: ForestProductResolver
        },
        children: [
          {
            path: '',
            component: BuyPermitsComponent
          }
        ]
      },
      {
        path: 'forests/:id/products/:product/permits/:permitId',
        title: 'USDA Forest Service E-Permits - Purchase Confirmation',
        children: [
          {
            path: '',
            resolve: {
              forest: ForestProductResolver,
              permit: PermitResolver
            },
            component: OrderConfirmationComponent
          }
        ]
      },
      {
        path: 'forests/:id/products/:product/permits/:permitId/paygov-error',
        title: 'USDA Forest Service E-Permits - Something Went Wrong!',
        children: [
          {
            path: '',
            resolve: {
              forest: ForestProductResolver,
              permit: PermitResolver,
            },
            component: PayGovErrorComponent
          }
        ]
      },
    ]
  },
  {
    path: 'admin',
    resolve: {
      siteSettings: SiteSettingsResolver,
    },
    runGuardsAndResolvers: "always",
    children: [
      {
        path: 'permits',
        title: 'USDA Forest Service E-Permits - Admin - Permit Dashboard',
        canActivate: [PermitManagementGuard],
        children: [
          {
            path: '',
            resolve: {
              sessionUser: UserResolver,
            },
            data: { breadcrumb: 'Admin-Permit Dashboard' },
            children: [
              {
                path: '',
                component: AdminPermitsComponent,
              },
              {
                path: ':id',
                component: AdminManagePermitsComponent,
                data: { breadcrumb: 'Permit Documents' },
              },
            ]
          },
        ]
      },
      {
        path: 'users',
        title: 'USDA Forest Service E-Permits - Admin - User Management',
        canActivate: [UserManagementGuard],
        children: [
          {
            path: '',
            resolve: {
              forests: ForestsResolver,
              sessionUser: UserResolver,
            },
            data: { breadcrumb: 'Admin-User Management' },
            children: [
              {
                path: '',
                component: AdminUsersComponent,
              },
              {
                path: 'add',
                component: AdminManageUsersComponent,
                canDeactivate: [CanDeactivateGuard],
                data: { breadcrumb: 'User Role Management' },
              },
              {
                path: ':id/edit',
                canDeactivate: [CanDeactivateGuard],
                component: AdminManageUsersComponent,
                data: { breadcrumb: 'User Role Management' },
              },
              {
                path: ':id',
                data: { breadcrumb: 'User Role Management' },
                component: AdminManageUsersComponent,
              },
            ]
          },
        ]
      },
      {
        path: 'settings',
        canActivate: [SiteSettingsGuard],
        children: [{
          path: '',
          component: SiteSettingsComponent,
          }, {
          path: 'editforest/:code',
          component: ModifyForestComponent
        }],
      }, {
        path: 'alerts',
        title: 'USDA Forest Service E-Permits - Admin - Manage Alerts',
        component: ManageAlertsComponent,
        canActivate: [SupportUserGuard],
      },
    ]
  },
  {
    path: 'mock-pay-gov',
    component: LandingPageComponent,
  },

  { path: 'logged-in', component: LoggedInComponent, data: { title: 'Logged in' } },
  { path: 'sign-in', component: SignInComponent, data: { title: 'Sign in' } },
  { path: 'style-guide', component: StyleGuideComponent, data: { title: 'Style guide' } },
  { path: 'access-denied', component: AccessDeniedComponent, data: { title: 'Access Denied' } },
  { path: '500', component: ServerErrorComponent, data: { title: '500 server error' } },
  { path: '404', component: NotFoundComponent, data: { title: '404 not found' } },
  { path: '**', component: NotFoundComponent, data: { title: '404 not found' } }
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes, { useHash: false, anchorScrolling: 'enabled' })],
  exports: [RouterModule],
  providers: [
    PermitResolver,
    ForestProductResolver,
    ForestsResolver,
    ForestsAdminResolver,
    {
      provide: UrlSerializer,
      useClass: LowerCaseAdminUrlSerializer
    }
  ],
})
export class AppRoutingModule {}
